import { Button, InlineNotification, RadioButton, RadioButtonGroup, TextInput, Toggle } from '@carbon/react';
import type { FieldEditorProps } from '@pos/admin-ui';
import { build } from '@pos/brand-tokens';
import { useTranslation, type I18nKey } from '@pos/i18n';
import { useMemo, type CSSProperties } from 'react';
import './pos.scss';

/**
 * The switches in a POS profile's settings that the POS and store backend read. Add one here with its vi/en text
 * (`posSettings.switches.<key>`, and `posSettings.help.<key>` when it needs explaining).
 * `invoiceQr`: receipts of orders without VAT details carry a QR to the brand website's VAT invoice form (FR-EIN-06, ADR 0095).
 */
const SWITCHES = ['allowTableSplit', 'invoiceQr'] as const;
const HELP: Partial<Record<(typeof SWITCHES)[number], I18nKey>> = { invoiceQr: 'posSettings.help.invoiceQr' };

/** When the receipt of a paid order prints (`receiptPrint`, FR-PAY-12); `auto`, the default, is left out of the JSON. */
const RECEIPT_MODES = ['auto', 'ask', 'off'] as const;

/** Where the picker opens while no accent is set: the POS's own default accent (pos-ui `accent.ts`). */
const PICKER_START = '#2563EB';

/** The profile's own POS accent (`accent`, ADR 0102): `#RRGGBB`, or left out for the brand's main colour. */
const HEX = /^#[0-9A-Fa-f]{6}$/;

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * The POS profile's settings (`config.pos_profile.settings`, jsonb) as toggles, like the profile's other yes/no fields, instead of raw JSON (ADR 0041).
 * Keys this screen doesn't know are kept as they are.
 */
export function ProfileSettingsEditor({ value, invalidText, onChange }: FieldEditorProps) {
  const { t } = useTranslation();
  const settings = useMemo(() => {
    if (value == null || value === '') return {};
    try {
      const parsed: unknown = typeof value === 'string' ? JSON.parse(value) : value;
      return isObject(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }, [value]);

  if (!settings) {
    return <InlineNotification kind="warning" lowContrast hideCloseButton title={t('posSettings.invalid')} />;
  }

  // An unticked switch is left out, so the JSON stays as short as before.
  const set = (key: string, checked: boolean) => {
    const next = Object.fromEntries(Object.entries(settings).filter(([k]) => k !== key));
    onChange(JSON.stringify(checked ? { ...next, [key]: true } : next));
  };

  const setReceipt = (mode: string) => {
    const next = Object.fromEntries(Object.entries(settings).filter(([k]) => k !== 'receiptPrint'));
    onChange(JSON.stringify(mode === 'auto' ? next : { ...next, receiptPrint: mode }));
  };
  const receipt = RECEIPT_MODES.find((m) => m === settings.receiptPrint) ?? 'auto';

  // The POS accent: what is typed goes in (the server refuses a wrong one too); empty leaves it out, so the brand's colour shows.
  const accent = typeof settings.accent === 'string' ? settings.accent : '';
  const setAccent = (text: string) => {
    const next = Object.fromEntries(Object.entries(settings).filter(([k]) => k !== 'accent'));
    const value = text.trim();
    onChange(JSON.stringify(value ? { ...next, accent: value } : next));
  };
  const accentValid = accent === '' || HEX.test(accent);

  return (
    <>
      {SWITCHES.map((key) => {
        const help = HELP[key];
        return (
          <div key={key} className="pos-settings__switch">
            <Toggle
              id={`pos-settings-${key}`}
              labelText={t(`posSettings.switches.${key}` as I18nKey)}
              labelA={t('admin.no')}
              labelB={t('admin.yes')}
              toggled={settings[key] === true}
              onToggle={(checked) => set(key, checked)}
              aria-describedby={help ? `pos-settings-${key}-help` : undefined}
            />
            {help && (
              <p id={`pos-settings-${key}-help`} className="pos-settings__help">
                {t(help)}
              </p>
            )}
          </div>
        );
      })}
      <RadioButtonGroup
        legendText={t('posSettings.receiptPrint.label')}
        name="pos-settings-receiptPrint"
        orientation="vertical"
        valueSelected={receipt}
        onChange={(mode) => setReceipt(String(mode))}
      >
        {RECEIPT_MODES.map((mode) => (
          <RadioButton
            key={mode}
            id={`pos-settings-receiptPrint-${mode}`}
            value={mode}
            labelText={t(`posSettings.receiptPrint.${mode}` as I18nKey)}
          />
        ))}
      </RadioButtonGroup>
      <div className="pos-settings__accent">
        <div className="pos-settings__accent-field">
          <TextInput
            id="pos-settings-accent"
            labelText={t('posSettings.accent.label')}
            helperText={t('posSettings.accent.help')}
            placeholder="#RRGGBB"
            value={accent}
            invalid={!accentValid}
            invalidText={t('posSettings.accent.invalid')}
            onChange={(e) => setAccent(e.target.value)}
          />
          {/* The browser's own picker (Carbon has none): it writes the same #RRGGBB, so typing and picking stay one value. */}
          <input
            type="color"
            className="pos-settings__accent-picker"
            aria-label={t('posSettings.accent.pick')}
            value={(accentValid && accent ? accent : PICKER_START).toLowerCase()}
            onChange={(e) => setAccent(e.target.value.toUpperCase())}
          />
        </div>
        {accent && accentValid && <AccentPreview colour={accent} />}
        {accent && (
          <Button kind="ghost" onClick={() => setAccent('')}>
            {t('posSettings.accent.useBrand')}
          </Button>
        )}
      </div>
      {invalidText && <InlineNotification kind="error" lowContrast hideCloseButton title={invalidText} />}
    </>
  );
}

/**
 * The accent the POS will paint, in its light and dark themes: from the same generator as the POS (`@pos/brand-tokens`), so a colour too
 * light for white text shows darkened here as it will on the terminals. The colours are the generator's, not Carbon tokens.
 */
function AccentPreview({ colour }: { colour: string }) {
  const { t } = useTranslation();
  const tokens = useMemo(() => build({ name: 'pos', primary: colour, fonts: {} }), [colour]);
  return (
    <div className="pos-settings__accent-preview" aria-hidden="true">
      {(['light', 'dark'] as const).map((theme) => (
        <span
          key={theme}
          className="pos-settings__accent-swatch"
          style={{ '--accent': tokens[theme].primary, '--on-accent': tokens[theme]['on-primary'] } as CSSProperties}
        >
          {t('pos.actions.labels.send')} · {t(theme === 'light' ? 'md.enums.theme.g10' : 'md.enums.theme.g100')}
        </span>
      ))}
    </div>
  );
}
